import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { TimePeriod } from "./TimePeriodSelector";
import {
  getPeriodBounds,
  isFuturePeriod,
  normalizeDateForPeriod,
  shiftDateByPeriod,
} from "./utils";

interface DateNavigationProps {
  currentDate: Date;
  selectedPeriod: TimePeriod;
  onDateChange: (date: Date) => void;
  showDatePicker: boolean;
  onShowDatePickerChange: (show: boolean) => void;
  darkMode?: boolean;
}

export const DateNavigation = ({
  currentDate,
  selectedPeriod,
  onDateChange,
  showDatePicker,
  onShowDatePickerChange,
  darkMode = false,
}: DateNavigationProps) => {
  const handlePreviousDate = () => {
    onDateChange(shiftDateByPeriod(selectedPeriod, currentDate, -1));
  };

  const handleNextDate = () => {
    const newDate = shiftDateByPeriod(selectedPeriod, currentDate, 1);

    if (!isFuturePeriod(selectedPeriod, newDate)) {
      onDateChange(newDate);
    }
  };

  const formatDateDisplay = (date: Date) => {
    switch (selectedPeriod) {
      case "day":
        return date.toLocaleDateString("en-US", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      case "week": {
        const { start, end } = getPeriodBounds("week", date);

        return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
      }
      case "month":
        return date.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      case "year":
        return date.getFullYear().toString();
      default:
        return date.toLocaleDateString("en-US", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        });
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      onDateChange(normalizeDateForPeriod(selectedPeriod, date));
      onShowDatePickerChange(false);
    }
  };

  return (
    <div className="flex items-center justify-between py-1.5">
      <Button
        variant="ghost"
        size="icon"
        onClick={handlePreviousDate}
        className={cn(
          "h-7 w-7",
          darkMode
            ? "text-muted-foreground hover:text-foreground hover:bg-muted"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        )}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <div className="text-center">
        {/* Make date picker available for ALL periods, not just day */}
        <Popover open={showDatePicker} onOpenChange={onShowDatePickerChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "text-sm font-medium px-3 py-1.5 rounded-md",
                darkMode
                  ? "text-foreground hover:bg-muted"
                  : "text-foreground hover:bg-muted"
              )}
            >
              {formatDateDisplay(currentDate)}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              "w-auto p-0",
              darkMode && "dark bg-background border-border"
            )}
            align="center"
          >
            <Calendar
              mode="single"
              selected={currentDate}
              onSelect={handleDateSelect}
              disabled={(date) =>
                date > new Date() || date < new Date("1900-01-01")
              }
              initialFocus
              className={darkMode ? "dark" : ""}
            />
          </PopoverContent>
        </Popover>
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={handleNextDate}
        className={cn(
          "h-7 w-7",
          darkMode
            ? "text-muted-foreground hover:text-foreground hover:bg-muted"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        )}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
